/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  BuilderFormSchemaFactory, FormRootSchema,
  FormStateSchema,
  JobFinderListItem,
  PbHousePlanFinderListItem,
} from '@gh/builder-shared';
import { FormState, toAppFormControl } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import {
  FormMode,
  getControl,
  getControlValue,
  markControlAsDirty,
  setControlValue,
} from '@gh/core-ui';

import { isNil } from 'lodash';

import { JobRedoDetails } from '../../resources/job-redo.model';

@Injectable()
export class JobRedoEstimateFormState extends FormState<JobRedoDetails> {
  constructor(schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  get job(): Maybe<JobFinderListItem> {
    return getControlValue(this.group, 'job');
  }

  get jobId(): Maybe<ID> {
    const { job } = this;
    return job ? job.id : void 0;
  }

  get isJobSelected(): boolean {
    return !isNil(getControlValue(this.group, 'job'));
  }

  get isPriceBookSelected(): boolean {
    return !isNil(getControlValue(this.group, 'job')) && !isNil(getControlValue(this.group, 'priceBook'));
  }

  get priceBook(): AutocompleteItem {
    return getControlValue(this.group, 'priceBook');
  }

  get housePlan(): PbHousePlanFinderListItem {
    return getControlValue(this.group, 'housePlan');
  }

  build(): void {
    this.initSchema(<any>{ showQQ: false });
  }

  reset(): void {
    setControlValue(this.group, 'job', void 0);
    this.resetPriceBook();
  }

  resetPriceBook(): void {
    setControlValue(this.group, 'priceBook', void 0);
    setControlValue(this.group, 'housePlan', void 0);
    this.updateEnabledState();
  }

  priceBookChanged(): void {
    setControlValue(this.group, 'housePlan', void 0);
    this.updateEnabledState();
  }

  setJob(job: JobFinderListItem): void {
    setControlValue(this.group, 'job', job);
    markControlAsDirty(this.group, 'job');
    setControlValue(this.group, 'priceBook', void 0);
    setControlValue(this.group, 'housePlan', void 0);
    this.updateEnabledState();
  }

  setHousePlan(hp: PbHousePlanFinderListItem): void {
    setControlValue(this.group, 'housePlan', hp);
    markControlAsDirty(this.group, 'housePlan');
  }

  protected buildSchema(_: JobRedoDetails, { root }: FormStateSchema): FormRootSchema {
    return root({
      enabled: true,
      mode: FormMode.EditMode,
      children: {
        job: void 0,
        priceBook: { enabled: false, required: true },
        showQQ: void 0,
        housePlan: { enabled: false, required: true },
      },
    });
  }

  private updateEnabledState(): void {
    toAppFormControl(getControl(this.group, 'priceBook')).updateSchema({ enabled: this.isJobSelected });
    toAppFormControl(getControl(this.group, 'housePlan')).updateSchema({ enabled: this.isPriceBookSelected });
  }
}
